import { Box, Collapse, Fade, Grid, IconButton, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import type { MeterInput, NewMeter } from '@weft/types';
import { AggregationWithFieldOperatorSchema } from '@weft/types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useToggle } from '../../../../hooks/use-toggle';
import { AggregationSelect } from './AggregationSelect';
import { MeterFieldSelect } from './MeterFieldSelect';
import { SectionContainer } from './SectionContainer';

export const CreateProductPageSummary: React.FC = () => {
  const { control, watch } = useFormContext<MeterInput, unknown, NewMeter>();
  const [collapseAdvanced, toggleCollapseAdvanced] = useToggle(false);

  const eventName = watch('eventName');
  const aggregationOperator = watch('aggregationMethod.operator');

  const isParameterizedAggregationMethod = checkIsParameterizedAggregationMethod(aggregationOperator);

  return (
    <SectionContainer
      title="Summary"
    >
      <Grid
        container
        direction="row"
        alignItems="end"
        columnGap={1.5}
      >
        <Grid item>
          <Box>
            <Typography
              noWrap
              marginBottom="8px"
            >
              Sum up
            </Typography>
          </Box>
        </Grid>

        <Grid
          item
          flex={1}
        >
          <Controller
            control={control}
            name="aggregationMethod.operator"
            render={({ field }) => (
              <AggregationSelect
                value={field.value}
                onChange={(event, newValue) => {
                  event.preventDefault();
                  field.onChange(newValue);
                }}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid
          item
          flex={1}
        >
          <Fade in={isParameterizedAggregationMethod}>
            <Box sx={{
              columnGap: '12px',
              display: 'flex',
              alignItems: 'end',
            }}
            >
              <Typography
                marginBottom="8px"
              >
                on
              </Typography>

              <Controller
                control={control}
                name="aggregationMethod.field"
                defaultValue=""
                render={({ field }) => (
                  <MeterFieldSelect
                    eventName={eventName}
                    value={field.value}
                    onChange={(event, newValue) => {
                      event.preventDefault();
                      field.onChange(newValue);
                    }}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Fade>
        </Grid>

        <Grid item>
          <Box>
            <IconButton
              size="small"
              sx={theme => ({
                color: collapseAdvanced ? theme.palette.primary.main : theme.palette.text.secondary,
                columnGap: '8px',
                marginBottom: '4px',
              })}
              onClick={() => toggleCollapseAdvanced()}
            >
              <VyIcon icon="Settings"/>
            </IconButton>
          </Box>
        </Grid>

        <Grid
          item
          container
          xs={12}
        >
          <Collapse in={collapseAdvanced}>
            <Grid
              item
              marginTop={2}
            >
              <Controller
                control={control}
                name="aggregationMethod.instanceKey"
                render={({ field }) => (
                  <MeterFieldSelect
                    eventName={eventName}
                    value={field.value}
                    onChange={(event, newValue) => {
                      event.preventDefault();
                      field.onChange(newValue);
                    }}
                    label="Group by"
                    sx={{
                      width: '240px',
                    }}
                  />
                )}
              />
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    </SectionContainer>
  );
};

function checkIsParameterizedAggregationMethod(aggregationOperator: string | undefined) {
  return AggregationWithFieldOperatorSchema.safeParse(aggregationOperator).success;
}